import { ChangeDetectorRef, Component, computed, DestroyRef, forwardRef, HostBinding, inject, input, OnInit, output, signal, viewChild } from '@angular/core';
import { MatSelectModule } from '@angular/material/select';
import { HumanPipe } from '../../../../shared/pipes/human.pipe';
import { NgClass } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, lastValueFrom, tap, throwError } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Client } from '../../../../models/client.model';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CurrencyMaskModule } from 'ng2-currency-mask';
import { FormGroupPipe } from '../../../../shared/pipes/form-group.pipe';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ClientSelectComponent } from '../../../../client/client-select/client-select.component';
import { ProviderSelectComponent } from '../../../../providers/provider-select/provider-select.component';
import moment from 'moment';
import { FinancialMovementService } from '../../financial-movement.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AdminService } from '../../../../admin/services/admin.service';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';

@Component({
  selector: 'app-financial-movement-create-form',
  imports: [
    forwardRef(() => ClientSelectComponent),
    CurrencyMaskModule,
    forwardRef(() => FormGroupPipe),
    FormsModule,
    HumanPipe,
    MatAutocompleteModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatDialogClose,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatRadioButton,
    MatRadioGroup,
    MatToolbarModule,
    MatTooltipModule,
    NgClass,
    forwardRef(() => ProviderSelectComponent),
    ReactiveFormsModule,
],
  templateUrl: './edit.component.html',
  styleUrl: './edit.component.scss',
})
export class FinancialMovementEditFormComponent implements OnInit {

  id = input<number>();
  
  isPluginMode = input<boolean>(false);

  workInstanceId = input<number | undefined>();


  updated = output<void>();


  alreadyPayed = signal<boolean>(true);

  clients = signal<Client[]>([]);

  private readonly destroyRef = inject(DestroyRef);

  private readonly dialogRef = inject(MatDialogRef<FinancialMovementEditFormComponent>);

  displayClientFn = (client_id) => this.clients()?.find((client) => client.id === client_id)?.name;

  private readonly financialMovementService = inject(FinancialMovementService);

  loading = signal<boolean>(false);

  selectedClient = signal<Client | undefined>(undefined);

  translatedTypeName: string;


  private readonly fb = inject(UntypedFormBuilder);
  

  form = this.fb.group({
    id: this.fb.control(null),
    party_id: this.fb.control(null, Validators.required),
    party_type: this.fb.control('Client', Validators.required),
    type: ['FinancialMovement::Prepayment', Validators.required],
    payment_method: this.fb.control(null, Validators.required),
    amount: this.fb.control(null, { nonNullable: true, validators: [Validators.required] }),    
    due_date: this.fb.control(null, { nonNullable: true, validators: [Validators.required] }),
    notes: this.fb.control(null),
    extra_params: this.fb.group({
      reference_number: null,
    }),
  });
  
  readonly data = inject(MAT_DIALOG_DATA);

  _hideClientSelection = this.data?.hideClientSelection;
  _id = computed(() => this.id() || this.data?.id);


  @HostBinding('class') background;


  private readonly adminService = inject(AdminService);

  private readonly cdr = inject(ChangeDetectorRef);

  private readonly snackBar = inject(MatSnackBar);

  
  get formValid() {
    return this.form.valid && (this._hideClientSelection || this.selectedClient());
  }

  get shouldEnterReceiptNumber(): boolean {
    return ['credit_card', 'debit_card'].includes(this.form.get('payment_method').value);
  }

  ngOnInit() {
    if (this.isPluginMode()) {
      this.background = 'plugin-mode';
      this.cdr.detectChanges();
    }

    this.load();

    this.setFormHandlers();
    this.setFormControlValidators();

    this.cdr.detectChanges();
  }

  private load() {
    if (this.data.financialMovement) {
      this.form.patchValue(this.data.financialMovement);

      const d = moment(this.data.financialMovement.due_date).toDate();
      this.form.get('due_date').setValue(d);
      this.translatedTypeName = this.adminService.getText(this.data.financialMovement.type, 'capitalize');
      this.cdr.detectChanges();
    } else {
      this.financialMovementService.get(this._id() || this.data.id).pipe(
        catchError((error) => {
          console.error('Error loading financial movement:', error);
          this.snackBar.open('Error al cargar el movimiento', 'Cerrar', { duration: 5000, panelClass: 'snackbar-error' });
          this.loading.set(false);
          return EMPTY;
        }),
      ).subscribe((response) => {
        this.form.patchValue(response.body);
        this.form.get('party_id').setValue(response.body.party);
  
        const d = moment(response.body.due_date).toDate();
        this.form.get('due_date').setValue(d);

        this.alreadyPayed.set(response.body.paid_at !== null);
        
        this.translatedTypeName = this.adminService.getText(response.body.type, 'capitalize');
        this.cdr.detectChanges();
      });
    }
  }

  private setFormHandlers() {
    this.form.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.updated.emit();
    });

    this.form.get('due_date').valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((date) => {
      if (date) {
        this.alreadyPayed.set(moment(date).isBefore(moment()));
      }
    });

    this.setClientFormHandlers();
    this.setPaymentFormHandlers();
  }

  onClientLoad(client: any) {
    if (!client) {
      this.selectedClient.set(undefined);
      return;
    }

    this.selectedClient.set(client);
  }

  private setClientFormHandlers() {
    if (this._hideClientSelection) {
      this.form.get('party_type').clearValidators();
      this.form.get('party_id').clearValidators();
      this.form.get('party_type').updateValueAndValidity();
      this.form.get('party_id').updateValueAndValidity();

      return;
    }

    this.form.get('party_type').valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.form.get('party_id').reset();
    });
  }

  private setPaymentFormHandlers() {
    this.form.get('payment_method').valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (this.shouldEnterReceiptNumber) {
        this.form.get('extra_params.reference_number').setValidators([Validators.required]);
      }
      else {
        this.form.get('extra_params.reference_number').clearValidators();
      }
      this.form.get('extra_params.reference_number').updateValueAndValidity();
    });
  }

  private setFormControlValidators() {}

  saveForm(): Promise<any> {

    this.loading.set(true);
    const formData = this.form.value;
    const body = formData;

    if (this.alreadyPayed()) {
      formData.paid_at = formData.due_date;
    }

    if (this.isPluginMode()) {
      body.work_instance_id = this.workInstanceId();
    }

    if (this.data?.commitMode === false) {
      this.dialogRef?.close(body);
      return;
    }

    return lastValueFrom(this.financialMovementService.update(this._id(), body).pipe(
        catchError((error) => {
          console.error('Error creating financial movement:', error);
          this.snackBar.open(`Error al editar ${this.translatedTypeName}`, 'Cerrar', { duration: 5000, panelClass: 'snackbar-error' });
          this.loading.set(false);
          return throwError(() => error);
        }),
        tap(() => {
          this.loading.set(false);
          if (!this.isPluginMode()) {
          this.snackBar.open(`${this.translatedTypeName} editado exitosamente`, 'Cerrar', { duration: 5000, panelClass: 'snackbar-success' });

            this.dialogRef?.close(true);
          }
        }),
    ));
  }
}
